import { MessageResponseWithBlocks } from "@/components/ai-elements/message-response-with-blocks";
import CardAIStream from "@/components/card-ai-stream";
import { LazyMount } from "@/components/lazy-mount";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useCompanySections } from "@/contexts/company-sections-context";
import { useChatActionEmitter } from "@/hooks/useChatActions";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO } from "@shaarp/common";
import { useCallback, useState } from "react";
import { TbCircleFilled } from "react-icons/tb";

type CompanyMobileAIInsightsProps = {
  readonly company: CompanyDTO;
};

export default function CompanyMobileAIInsights({
  company,
}: CompanyMobileAIInsightsProps) {
  const { t } = useTranslation();
  // The advice reads the whole sheet server-side: it waits for the run.
  const { done } = useCompanySections();

  return (
    <div className="px-4 flex flex-col gap-4">
      <LazyMount>
        <TruncatedAIInsightCard
          title={t("company.insights.aiAdvice")}
          streamUrl={`/company/${company.id}/advice`}
          launchFetch={done}
        />
      </LazyMount>
    </div>
  );
}

type TruncatedAIInsightCardProps = {
  readonly title: string;
  readonly streamUrl: string;
  readonly launchFetch: boolean;
};

function TruncatedAIInsightCard({
  title,
  streamUrl,
  launchFetch,
}: TruncatedAIInsightCardProps) {
  const { t } = useTranslation();
  const { sendChatMessage, isPending: isChatPending } = useChatActionEmitter();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [text, setText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  const handleTextChange = useCallback(
    (nextText: string, nextIsComplete: boolean) => {
      setText(nextText);
      setIsComplete(nextIsComplete);
    },
    [],
  );

  const handleSuggestionClick = (prompt: string) => {
    if (!isChatPending) {
      sendChatMessage(prompt);
      setIsSheetOpen(false);
    }
  };

  return (
    <>
      <CardAIStream
        title={title}
        streamUrl={streamUrl}
        launchFetch={launchFetch}
        showAvatar={false}
        truncated
        onCardClick={() => setIsSheetOpen(true)}
        onTextChange={handleTextChange}
        hideSuggestions
      />

      <MobileSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <div className="px-5 pt-3 pb-4 shrink-0">
          <SheetTitle className="text-2xl font-semibold tracking-tight pr-8">
            {title}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t("company.mobile.aiInsights.sheetDescription")}
          </SheetDescription>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 pb-5">
          {text ? (
            <>
              <MessageResponseWithBlocks
                onSuggestionClick={handleSuggestionClick}
                disabled={isChatPending}
                isStreaming={!isComplete}
                hideSuggestions
              >
                {text}
              </MessageResponseWithBlocks>
              {!isComplete && (
                <TbCircleFilled className="animate-pulse mt-2" size={20} />
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-12">
              {t("company.mobile.aiInsights.sheetLoading")}
            </p>
          )}
        </div>
      </MobileSheet>
    </>
  );
}
